/**
 * 简化的同步Electron IPC工具
 * 直接使用window.flowUsIpcRenderer，避免复杂的异步初始化
 */

// 导入shared包的IPC消息类型
import { IpcMessage } from '@flowus/shared';

// IPC通道类型 - 使用shared包中的定义
type IPCChannel =
  // 下载相关 - 使用shared包枚举
  | typeof IpcMessage.DownloadUrl
  | typeof IpcMessage.DownloadMany
  | typeof IpcMessage.GetDownloadTasks
  | typeof IpcMessage.CancelDownload
  | typeof IpcMessage.RemoveDownload
  | typeof IpcMessage.DownloadTasksChanged
  | typeof IpcMessage.RemoveDownloadTask
  | typeof IpcMessage.GetDownloadTasksStatus
  // FlowUs应用通道 - 与shared包保持一致
  | typeof IpcMessage.GetAppVersion
  | typeof IpcMessage.OnFullScreenToggle
  | typeof IpcMessage.UpdateDownloadComplete
  | typeof IpcMessage.UpdateAvailable
  | typeof IpcMessage.UpdateNotAvailable
  | typeof IpcMessage.IsFullScreen
  | typeof IpcMessage.CheckForUpdates
  | typeof IpcMessage.Log
  | typeof IpcMessage.Message
  | typeof IpcMessage.OnOpenSetting
  | typeof IpcMessage.CanHistoryBackAndForward
  | typeof IpcMessage.CopyText
  | typeof IpcMessage.CopyImage
  | typeof IpcMessage.ClearCacheAndReload;

interface SimpleIPCApi {
  invoke(channel: IPCChannel, ...args: any[]): Promise<any>;
  send(channel: IPCChannel, ...args: any[]): void;
  on(channel: IPCChannel, callback: (...args: any[]) => void): void;
  removeListener(channel: IPCChannel, callback: Function): void;
  isReady(): boolean;
  version: string;
}

/**
 * 获取IPC API - 同步方式
 */
function getIPCApi(): SimpleIPCApi | null {
  const api = (window as any).flowUsIpcRenderer;
  if (api && api.isReady && api.isReady()) {
    return api;
  }
  return null;
}

/**
 * 检查是否在Electron应用中
 */
export function isFlowUsApp(): boolean {
  const api = getIPCApi();
  return api !== null;
}

/**
 * IPC invoke调用
 */
export async function ipcInvoke(channel: IPCChannel, ...args: any[]): Promise<any> {
  const api = getIPCApi();
  if (!api) {
    throw new Error('IPC API not available');
  }
  return api.invoke(channel, ...args);
}

/**
 * IPC send调用
 */
export function ipcSend(channel: IPCChannel, ...args: any[]): void {
  const api = getIPCApi();
  if (!api) {
    console.warn('IPC API not available, ignoring send');
    return;
  }
  api.send(channel, ...args);
}

/**
 * IPC事件监听
 */
export function ipcOn(channel: IPCChannel, callback: (...args: any[]) => void): void {
  const api = getIPCApi();
  if (!api) {
    console.warn('IPC API not available, ignoring listener');
    return;
  }
  api.on(channel, callback);
}

/**
 * 移除IPC事件监听
 */
export function ipcOff(channel: IPCChannel, callback: Function): void {
  const api = getIPCApi();
  if (!api) {
    return;
  }
  api.removeListener(channel, callback);
}

// 向后兼容
export const electronIPC = {
  isElectronApp: isFlowUsApp,
  invoke: ipcInvoke,
  send: ipcSend,
  on: ipcOn,
  off: ipcOff,
};

// 兼容旧版本导出
export const isFlowUsApp_old = {
  get check() {
    return isFlowUsApp();
  },
};

export const getIpcRenderer = () => {
  return getIPCApi();
};
